import type { Signal } from '@/types'

interface SignalListProps {
  signals: Signal[]
  isLoading?: boolean
  title?: string
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()

  if (isToday) {
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  return date.toLocaleString('ru-RU', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-'
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${minutes}m ${secs}s`
}

function SignalRow({ signal }: { signal: Signal }) {
  const isAsk = signal.side === 'ask' || signal.side === 'sell'
  const isDom = signal.source === 'dom'
  const domMode = isDom ? signal.dom_mode : null

  return (
    <div
      className={`
        flex items-center gap-3 px-3 py-2 rounded-lg bg-chart-grid
        border-l-4
        ${isAsk ? 'border-ask' : 'border-bid'}
      `}
    >
      {/* Source badge */}
      <div
        className={`
          px-2 py-0.5 rounded text-xs font-medium uppercase
          ${isDom ? 'bg-purple-500/20 text-purple-400' : 'bg-neutral-400/20 text-neutral-300'}
        `}
      >
        {signal.source}
      </div>

      {/* DOM mode badge */}
      {domMode && (
        <div
          title={domMode === 'aggregated' ? 'Агрегированные' : 'Точные'}
          className={`
            px-1.5 py-0.5 rounded text-[10px] font-medium
            ${domMode === 'aggregated'
              ? 'bg-amber-500/15 text-amber-400 border border-dashed border-amber-500/40'
              : 'bg-sky-500/15 text-sky-400 border border-sky-500/40'}
          `}
        >
          {domMode === 'aggregated' ? 'AGG' : 'PREC'}
        </div>
      )}

      {/* Side indicator */}
      <div
        className={`
          w-8 text-center font-bold text-sm
          ${isAsk ? 'text-ask' : 'text-bid'}
        `}
      >
        {signal.side.toUpperCase()}
      </div>

      {/* Price and quantity */}
      <div className="flex-1">
        <div className="text-text-primary font-mono">
          {signal.quantity.toFixed(2)} BTC @ ${signal.price.toFixed(0)}
        </div>
        <div className="text-text-secondary text-xs">
          {formatTime(signal.detected_at)}
          {signal.duration_seconds !== null && (
            <span className="ml-2">
              ({formatDuration(signal.duration_seconds)})
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export function SignalList({ signals, isLoading, title = 'Signals' }: SignalListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-text-secondary">
        Loading signals...
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-chart-grid">
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
        <span className="text-sm text-text-secondary">
          {signals.length} signals
        </span>
      </div>

      {/* Signal list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {signals.length === 0 ? (
          <div className="text-center text-text-secondary py-8">
            No signals in this time range
          </div>
        ) : (
          signals.map((signal) => (
            <SignalRow key={signal.id} signal={signal} />
          ))
        )}
      </div>

      {/* Legend */}
      <div className="px-4 py-2 border-t border-chart-grid">
        <div className="flex items-center justify-center gap-4 text-xs text-text-secondary">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-purple-500/20" />
            <span>DOM</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-neutral-400/20 border border-neutral-500" />
            <span>Trade</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-1 bg-bid" />
            <span>Bid/Buy</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-1 bg-ask" />
            <span>Ask/Sell</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignalList
